import { Fragment, type ReactNode } from 'react';
import type { CustomEmoji } from '@avslack/shared';
import { CUSTOM_EMOJI_RE, useCustomEmoji } from '../data/custom-emoji';

/**
 * Egyetlen egyedi emoji kepe.
 *
 * Az `align-text-bottom` es a szoveghez kotott meret azert kell, hogy a kep a
 * sor kozepen uljon, es a szoveg meretevel egyutt nojon - kulonben egy nagyobb
 * betumereten aprónak latszana.
 */
export function CustomEmojiImage({
  emoji,
  large = false,
}: {
  emoji: CustomEmoji;
  /**
   * Az UZENETBEN ketszeres meret - a "csak emoji" uzenetben is, a nagyitott
   * betuhoz merve: egy sajat kep (logo, arc) a betu magassagaban
   * felismerhetetlenul kicsi volt. A reakcioknal es a valasztoban marad a
   * kisebb.
   */
  large?: boolean;
}) {
  return (
    <img
      src={emoji.url}
      alt={`:${emoji.name}:`}
      title={`:${emoji.name}:`}
      loading="lazy"
      className={`avslack-custom-emoji inline-block object-contain ${
        large ? 'h-[2.6em] w-[2.6em] align-middle' : 'h-[1.3em] w-[1.3em] align-text-bottom'
      }`}
    />
  );
}

/**
 * `:nev:` hivatkozasok kepre cserelese egy szovegben.
 *
 * Amit nem ismerunk, azt VALTOZATLANUL hagyjuk. Egy magyar mondatban a
 * kettospont gyakori (`10:30`, `igy: ...`), es rosszabb lenne eltuntetni egy
 * szovegreszt, mint kihagyni egy emojit.
 */
export function withCustomEmoji(
  text: string,
  byName: Map<string, CustomEmoji>,
  keyPrefix = 'e',
): ReactNode {
  if (!text.includes(':') || byName.size === 0) return text;

  const parts: ReactNode[] = [];
  let last = 0;
  let index = 0;

  // A globalis regex allapotot tart - minden hivasnal nullazzuk.
  CUSTOM_EMOJI_RE.lastIndex = 0;

  for (let match = CUSTOM_EMOJI_RE.exec(text); match; match = CUSTOM_EMOJI_RE.exec(text)) {
    const emoji = byName.get(match[1]!);
    if (!emoji) continue;

    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(<CustomEmojiImage key={`${keyPrefix}-${index}`} emoji={emoji} large />);
    last = match.index + match[0].length;
    index += 1;
  }

  if (parts.length === 0) return text;
  if (last < text.length) parts.push(text.slice(last));

  return parts.map((part, i) => <Fragment key={`${keyPrefix}-p-${i}`}>{part}</Fragment>);
}

/**
 * Reakcio-jel megjelenitese.
 *
 * A reakcio lehet Unicode-emoji vagy `:nev:` alaku egyedi emoji - a tarolt
 * ertek mindketto esetben csak egy rovid string, a kulonbseg itt dol el.
 */
export function ReactionGlyph({ emoji }: { emoji: string }) {
  const { byName } = useCustomEmoji();
  const match = /^:([a-z0-9][a-z0-9_-]{0,30}):$/.exec(emoji);
  const custom = match ? byName.get(match[1]!) : undefined;

  if (custom) return <CustomEmojiImage emoji={custom} />;
  return <span aria-hidden="true">{emoji}</span>;
}

// ─── "Csak egy emoji" ─────────────────────────────────────────────────────────

/**
 * Ennyi emojiig nagyitunk.
 *
 * Egy-ket jel meg gesztus ("igen", "szuper"), otnel mar uzenet - azt nem
 * kiabalni kell, hanem olvasni. A Messenger is harom korul huzza meg.
 */
const MAX_JUMBO = 3;

/** Billentyu-emoji (1️⃣, #️⃣): a szamjegy maga nem kepi jel, a burkolat teszi azza. */
const BILLENTYU = /^[0-9#*]\uFE0F?\u20E3$/;

/** Amibol egy emoji-grafema allhat: kepi jel, zaszlo-betu, bortonus, valaszto, osszekoto. */
const EMOJI_RESZEK =
  /^[\p{Extended_Pictographic}\p{Regional_Indicator}\p{Emoji_Modifier}\uFE0F\u200D]+$/u;

/** Legalabb egy TENYLEGES kep kell bele - a puszta valaszto-karakter nem emoji. */
const VAN_KEP = /[\p{Extended_Pictographic}\p{Regional_Indicator}]/u;

function emojiGrafema(grafema: string): boolean {
  if (BILLENTYU.test(grafema)) return true;
  return EMOJI_RESZEK.test(grafema) && VAN_KEP.test(grafema);
}

/**
 * Grafemakra bontas.
 *
 * KODPONTONKENT nem lehet: egy csaladot abrazolo emoji (👨‍👩‍👧) ot kodpont, egy
 * bortonusos kez ketto. Kodpontonkent szamolva ezek tobb emojinak latszananak,
 * es a hatar folott elmaradna a nagyitas.
 *
 * Ahol a bongeszo nem ismeri a `Segmenter`-t, kodpontonkent bontunk - ilyenkor
 * az osszetett emojik egyszeruen nem nagyodnak fel. Ez a biztonsagos irany:
 * inkabb marad kicsi, mint hogy egy hosszu szoveg ugorjon negyven pontosra.
 */
function grafemak(text: string): string[] {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const bonto = new Intl.Segmenter('hu', { granularity: 'grapheme' });
    return [...bonto.segment(text)].map((resz) => resz.segment);
  }
  return [...text];
}

/**
 * Hany emojibol all az uzenet, ha CSAK emojibol all.
 *
 * `0`, ha barmi mas is van benne, vagy ha tobb emoji, mint {@link MAX_JUMBO}.
 * A hivo ebbol valaszt betumeretet - lasd `MessageRow`.
 *
 * A sajat (`:nev:`) emojik is szamitanak, de csak azok, amiket ISMERUNK: egy
 * ismeretlen `:valami:` szovegkent jelenik meg, tehat az uzenet nem
 * "csak emoji". Ugyanaz a szabaly, mint a megjelenitesnel (`withCustomEmoji`).
 */
export function jumboEmojiCount(text: string, byName: Map<string, CustomEmoji>): number {
  const trimmelt = text.trim();
  if (!trimmelt) return 0;

  let darab = 0;
  let maradek = trimmelt;

  if (byName.size > 0 && maradek.includes(':')) {
    maradek = maradek.replace(CUSTOM_EMOJI_RE, (egesz, nev: string) => {
      if (!byName.has(nev)) return egesz;
      darab += 1;
      return ' ';
    });
  }

  for (const grafema of grafemak(maradek)) {
    if (!grafema.trim()) continue;
    if (!emojiGrafema(grafema)) return 0;
    darab += 1;
  }

  return darab > 0 && darab <= MAX_JUMBO ? darab : 0;
}
